// Expense Report — read-only analytics for the SUPERADMIN.
// Check the day's / any period's spending, drill into one category (e.g. Tea),
// and group the trend daily / weekly / monthly. Entry of expenses stays in the
// dedicated Expenses workspace; this screen is check-only.

import React, { useCallback, useEffect, useState } from 'react';
import {
  FiCalendar,
  FiDownload,
  FiLayers,
  FiList,
  FiPieChart,
  FiRefreshCw,
  FiTag,
  FiTrendingDown,
} from 'react-icons/fi';
import MainLayout from '../components/layout/MainLayout.jsx';
import { Alert } from '../components/common/index.js';
import axiosInstance from '../api/axiosInstance';
import { API_ENDPOINTS } from '../config/api.config';
import { downloadExpensesWorkbook } from '../utils/expenseExcel';

const fmtMoney = (v) => {
  if (v == null || v === '') return '₹0';
  const n = Number(v);
  return Number.isFinite(n) ? `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}` : '₹0';
};

const ymd = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// From-date for each quick preset (to = open-ended = up to now).
const presetRange = (preset) => {
  const now = new Date();
  // Today is the only preset that closes the far end — the rest run up to now.
  if (preset === 'today') { const today = ymd(now); return { from: today, to: today }; }
  if (preset === 'year') return { from: ymd(new Date(now.getFullYear(), 0, 1)), to: '' };
  if (preset === 'month') return { from: ymd(new Date(now.getFullYear(), now.getMonth(), 1)), to: '' };
  if (preset === 'week') {
    const dow = (now.getDay() + 6) % 7; // Monday = 0
    const monday = new Date(now);
    monday.setDate(now.getDate() - dow);
    return { from: ymd(monday), to: '' };
  }
  return { from: '', to: '' }; // all time
};

const PRESETS = [
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This week' },
  { value: 'month', label: 'This month' },
  { value: 'year', label: 'This year' },
  { value: 'all', label: 'All time' },
];
const GROUPS = [
  { value: 'day', label: 'Daily' },
  { value: 'week', label: 'Weekly' },
  { value: 'month', label: 'Monthly' },
];

const StatCard = ({ icon: Icon, label, value, tone = 'rose' }) => {
  const tones = {
    rose: 'bg-rose-50 text-rose-600',
    violet: 'bg-violet-50 text-violet-600',
    slate: 'bg-slate-100 text-slate-600',
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-3">
        <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${tones[tone]}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <p className="truncate text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</p>
          <p className="truncate text-lg font-bold text-slate-900">{value}</p>
        </div>
      </div>
    </div>
  );
};

const ExpenseReportPage = () => {
  const [preset, setPreset] = useState('month');
  const [custom, setCustom] = useState(false);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [group, setGroup] = useState('day');
  const [title, setTitle] = useState(''); // '' = all categories
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);

  const range = custom ? { from, to } : presetRange(preset);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { group };
      if (range.from) params.from = range.from;
      if (range.to) params.to = range.to;
      if (title) params.title = title;
      const res = await axiosInstance.get(API_ENDPOINTS.EXPENSES.REPORT, { params });
      setData(res.data ?? res);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Could not load the expense report');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group, range.from, range.to, title]);

  useEffect(() => { load(); }, [load]);

  // The screen shows the newest 500 rows; the file must contain every one, so
  // this re-fetches with full=1 rather than exporting what is already on screen.
  const downloadExcel = async () => {
    setDownloading(true);
    setError('');
    try {
      const params = { group, full: 1 };
      if (range.from) params.from = range.from;
      if (range.to) params.to = range.to;
      if (title) params.title = title;
      const res = await axiosInstance.get(API_ENDPOINTS.EXPENSES.REPORT, { params });
      downloadExpensesWorkbook(res.data ?? res, {
        preset: custom ? 'custom' : preset,
        from: range.from,
        to: range.to,
        category: title,
        group,
      });
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Could not build the Excel file');
    } finally {
      setDownloading(false);
    }
  };

  const series = data?.series ?? [];
  const byCat = data?.by_category ?? [];
  const items = data?.items ?? [];
  const categories = data?.categories ?? [];
  const maxCat = Math.max(1, ...byCat.map((c) => Number(c.total) || 0));
  const maxSeries = Math.max(1, ...series.map((s) => Number(s.total) || 0));

  const groupNoun = group === 'day' ? 'days' : group === 'week' ? 'weeks' : 'months';

  return (
    <MainLayout>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-black text-slate-900">Expense Report</h1>
          <p className="mt-1 text-sm text-slate-500">
            Check spending by day / week / month and drill into any category — read-only
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button" onClick={load}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-sm font-semibold text-slate-600 hover:border-violet-300"
          >
            <FiRefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
          <button
            type="button" onClick={downloadExcel} disabled={loading || downloading}
            className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-3.5 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
            title="Download every expense in this period as Excel"
          >
            <FiDownload className="h-4 w-4" /> {downloading ? 'Preparing…' : 'Download Excel'}
          </button>
        </div>
      </div>

      {error && <div className="mb-4"><Alert type="error" message={error} /></div>}

      {/* Filters */}
      <div className="mb-5 space-y-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        {/* Period presets */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="mr-1 inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-slate-400">
            <FiCalendar className="h-3.5 w-3.5" /> Period
          </span>
          {PRESETS.map((p) => {
            const active = !custom && preset === p.value;
            return (
              <button
                key={p.value} type="button"
                onClick={() => { setCustom(false); setPreset(p.value); }}
                className={`rounded-full border px-3 py-1.5 text-sm font-bold transition-all ${active ? 'border-violet-500 bg-violet-600 text-white shadow-sm' : 'border-slate-200 bg-white text-slate-600 hover:border-violet-300'}`}
              >
                {p.label}
              </button>
            );
          })}
          <button
            type="button" onClick={() => setCustom(true)}
            className={`rounded-full border px-3 py-1.5 text-sm font-bold transition-all ${custom ? 'border-violet-500 bg-violet-600 text-white shadow-sm' : 'border-slate-200 bg-white text-slate-600 hover:border-violet-300'}`}
          >
            Custom
          </button>
          {custom && (
            <div className="flex items-center gap-2">
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
                className="rounded-xl border border-slate-200 px-2.5 py-1.5 text-sm text-slate-600 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-300" />
              <span className="text-slate-400">–</span>
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
                className="rounded-xl border border-slate-200 px-2.5 py-1.5 text-sm text-slate-600 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-300" />
            </div>
          )}
        </div>

        {/* Group + Category */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="mr-1 inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-slate-400">
              <FiLayers className="h-3.5 w-3.5" /> Group by
            </span>
            <div className="inline-flex rounded-xl border border-slate-200 p-0.5">
              {GROUPS.map((g) => (
                <button
                  key={g.value} type="button" onClick={() => setGroup(g.value)}
                  className={`rounded-lg px-3 py-1.5 text-sm font-bold transition-all ${group === g.value ? 'bg-violet-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  {g.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span className="mr-1 inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-slate-400">
              <FiTag className="h-3.5 w-3.5" /> Category
            </span>
            <select
              value={title} onChange={(e) => setTitle(e.target.value)}
              className="rounded-xl border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-700 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-300"
            >
              <option value="">All categories</option>
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatCard icon={FiTrendingDown} label={title ? `${title} · total` : 'Total spent'} value={fmtMoney(data?.total)} tone="rose" />
        <StatCard icon={FiList} label="Entries" value={data ? String(data.count) : '—'} tone="slate" />
        <StatCard icon={FiPieChart} label={`Across ${groupNoun}`} value={data ? String(series.length) : '—'} tone="violet" />
      </div>

      {loading ? (
        <div className="py-16 text-center text-sm text-slate-400">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          {/* By category */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="mb-3 text-sm font-bold text-slate-900">By category</h2>
            {byCat.length === 0 ? (
              <p className="py-6 text-center text-sm text-slate-400">No expenses for this filter.</p>
            ) : (
              <div className="space-y-2.5">
                {byCat.map((c) => (
                  <button
                    key={c.title} type="button"
                    onClick={() => setTitle(title === c.title ? '' : c.title)}
                    className="block w-full text-left"
                    title={title === c.title ? 'Clear filter' : `Filter to ${c.title}`}
                  >
                    <div className="mb-1 flex items-center justify-between text-sm">
                      <span className={`font-semibold ${title === c.title ? 'text-violet-700' : 'text-slate-700'}`}>{c.title}</span>
                      <span className="font-bold text-slate-900">{fmtMoney(c.total)} <span className="text-xs font-medium text-slate-400">· {c.count}</span></span>
                    </div>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
                      <div className="h-full rounded-full bg-violet-500" style={{ width: `${Math.max(3, (Number(c.total) / maxCat) * 100)}%` }} />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Period series */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="mb-3 text-sm font-bold text-slate-900">
              {group === 'day' ? 'Day by day' : group === 'week' ? 'Week by week' : 'Month by month'}
            </h2>
            {series.length === 0 ? (
              <p className="py-6 text-center text-sm text-slate-400">No expenses for this filter.</p>
            ) : (
              <div className="space-y-2.5">
                {series.map((s) => (
                  <div key={s.key}>
                    <div className="mb-1 flex items-center justify-between text-sm">
                      <span className="font-semibold text-slate-700">{s.label}</span>
                      <span className="font-bold text-slate-900">{fmtMoney(s.total)} <span className="text-xs font-medium text-slate-400">· {s.count}</span></span>
                    </div>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
                      <div className="h-full rounded-full bg-rose-400" style={{ width: `${Math.max(3, (Number(s.total) / maxSeries) * 100)}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Raw entries — day by day */}
      {!loading && items.length > 0 && (
        <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="mb-3 text-sm font-bold text-slate-900">Entries ({items.length}{items.length === 500 ? '+ , showing latest 500' : ''})</h2>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[420px] text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-left text-[11px] font-bold uppercase tracking-wide text-slate-400">
                  <th className="py-2 pr-3">Date</th>
                  <th className="py-2 pr-3">Category</th>
                  <th className="py-2 pr-3 text-right">Amount</th>
                  <th className="py-2 pl-3">By</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} className="border-b border-slate-50 last:border-0">
                    <td className="py-2 pr-3 whitespace-nowrap text-slate-500">{it.date_display}</td>
                    <td className="py-2 pr-3 font-semibold text-slate-800">{it.title}</td>
                    <td className="py-2 pr-3 text-right font-bold text-slate-900">{fmtMoney(it.amount)}</td>
                    <td className="py-2 pl-3 text-slate-400">{it.added_by || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default ExpenseReportPage;
